import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export interface SparePart {
  id: string;
  order_id: string;
  description: string;
  status: string;
  tracking_number: string | null;
  created_at: string;
  updated_at: string;
}

export interface SparePartsFilters {
  organizationId: string | null;
  orderId?: string | null;
  status?: string | null;
  search?: string | null;
  page?: number;
}

interface UseSparePartsListResult {
  spareParts: SparePart[];
  totalCount: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

const PAGE_SIZE = 20;

export function useSparePartsList(filters: SparePartsFilters): UseSparePartsListResult {
  const {
    organizationId,
    orderId = null,
    status = null,
    search = null,
    page = 0,
  } = filters;

  const [spareParts, setSpareParts] = useState<SparePart[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const refetch = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  useEffect(() => {
    if (!organizationId) {
      setSpareParts([]);
      setTotalCount(0);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchSpareParts() {
      setIsLoading(true);
      setError(null);

      try {
        const start = page * PAGE_SIZE;
        const end = start + PAGE_SIZE - 1;

        let query = supabase
          .from('spare_parts')
          .select(`
            *,
            orders!inner (
              branch_id,
              branches!inner (
                organization_id
              )
            )
          `, { count: 'exact' })
          .eq('orders.branches.organization_id', organizationId);

        // Filter by order_id
        if (orderId) {
          query = query.eq('order_id', orderId);
        }

        // Filter by status
        if (status) {
          query = query.eq('status', status);
        }

        // Search by description
        if (search) {
          query = query.or(`description.ilike.%${search}%`);
        }

        // Order by created_at descending
        query = query.order('created_at', { ascending: false });

        // Pagination
        const { data, error: fetchError, count } = await query.range(start, end);

        if (fetchError) {
          if (!cancelled) {
            setError(fetchError.message);
            setSpareParts([]);
            setTotalCount(0);
          }
          return;
        }

        if (!cancelled) {
          setSpareParts((data as SparePart[]) || []);
          setTotalCount(count || 0);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError('Failed to fetch spare parts');
          setSpareParts([]);
          setTotalCount(0);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchSpareParts();

    return () => {
      cancelled = true;
    };
  }, [organizationId, orderId, status, search, page, refreshTrigger]);

  return {
    spareParts,
    totalCount,
    isLoading,
    error,
    refetch,
  };
}
